/**
 * Versioning Types and Functions
 * Requirements: 17.1, 17.2, 17.3, 18.6, 18.7, 18.8
 */

import type { ExportedCourse, ExportedLesson, Lesson } from './course';
import type { CourseBlock } from './blocks';

export interface VersionChangeReport {
  addedBlocks: string[];
  modifiedBlocks: string[];
  removedBlocks: string[];
  summary: string;
}

export interface CourseVersion {
  id: string;
  courseId: string;
  versionNumber: number;
  publishedBy: string;
  publishedAt: string;
  isMajorEdit: boolean;
  label?: string;
  snapshot: ExportedCourse;
  changeReport?: VersionChangeReport;
}

export interface LessonVersion {
  id: string;
  lessonId: string;
  versionNumber: number;
  publishedBy: string;
  publishedAt: string;
  isMajorEdit: boolean;
  label?: string;
  snapshot: ExportedLesson;
  changeReport?: VersionChangeReport;
}

/**
 * Input for creating a new version
 */
export interface CreateVersionInput {
  courseId: string;
  publishedBy: string;
  isMajorEdit?: boolean;
  label?: string;
  snapshot: ExportedCourse;
  previousVersion?: CourseVersion;
}

export interface CreateLessonVersionInput {
  lessonId: string;
  publishedBy: string;
  isMajorEdit?: boolean;
  label?: string;
  snapshot: ExportedLesson;
  previousVersion?: LessonVersion;
}

/**
 * Generate a unique version ID
 */
export function generateVersionId(): string {
  return `ver_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Convert normalized content state to ExportedCourse format
 * Requirements: 18.6
 */
export function contentStateToExportedCourse(
  courseMeta: {
    id: string;
    numericId?: number;
    title: string;
    description: string;
    completionCertificateEnabled?: boolean;
    certificateMessage?: string;
    completedMessage?: string;
    timedCourse?: boolean;
    durationMinutes?: number | null;
    minimumRequiredMinutes?: number | null;
    locale?: string;
    defaultLocale?: string;
    availableLocales?: ExportedCourse['availableLocales'];
  },
  lessons: Record<string, Lesson>,
  blocks: Record<string, CourseBlock>,
  lessonOrder: string[]
): ExportedCourse {
  // Convert lessons from record to array, preserving order
  const lessonsArray = lessonOrder
    .map(id => lessons[id])
    .filter((lesson): lesson is Lesson => lesson !== undefined);

  // Collect all blocks from lessons
  const blocksArray: CourseBlock[] = [];
  for (const lesson of lessonsArray) {
    for (const blockId of lesson.blockOrder) {
      const block = blocks[blockId];
      if (block) {
        blocksArray.push(block);
      }
    }
  }

  return {
    id: courseMeta.id,
    numericId: courseMeta.numericId,
    title: courseMeta.title,
    description: courseMeta.description,
    locale: courseMeta.locale,
    defaultLocale: courseMeta.defaultLocale,
    availableLocales: courseMeta.availableLocales,
    lessons: lessonsArray,
    blocks: blocksArray,
    completionCertificateEnabled: courseMeta.completionCertificateEnabled,
    certificateMessage: courseMeta.certificateMessage,
    completedMessage: courseMeta.completedMessage,
    timedCourse: courseMeta.timedCourse,
    durationMinutes: courseMeta.durationMinutes,
    minimumRequiredMinutes: courseMeta.minimumRequiredMinutes,
  };
}

/**
 * Convert a single lesson (normalized) to ExportedLesson format
 */
export function lessonStateToExportedLesson(
  lesson: Lesson,
  blocks: Record<string, CourseBlock>
): ExportedLesson {
  const blocksArray: CourseBlock[] = [];
  for (const blockId of lesson.blockOrder) {
    const block = blocks[blockId];
    if (block) {
      blocksArray.push(block);
    }
  }

  return {
    ...lesson,
    blocks: blocksArray,
  };
}

/**
 * Convert a single lesson (normalized) to a one-lesson ExportedCourse
 * Useful for lesson-only preview/export flows.
 */
export function lessonStateToExportedCourse(
  lesson: Lesson,
  blocks: Record<string, CourseBlock>
): ExportedCourse {
  const exportedLesson = lessonStateToExportedLesson(lesson, blocks);
  const { blocks: exportedBlocks, ...resolvedLesson } = exportedLesson;

  return {
    id: exportedLesson.id,
    title: exportedLesson.title,
    description: exportedLesson.description,
    lessons: [resolvedLesson],
    blocks: exportedBlocks,
  };
}


/**
 * Compare two blocks to check if they are different
 */
function areBlocksDifferent(block1: CourseBlock, block2: CourseBlock): boolean {
  // Compare by stringifying (excluding updatedAt which changes on every edit)
  const normalize = (block: CourseBlock) => {
    const { updatedAt, ...rest } = block;
    return JSON.stringify(rest);
  };
  return normalize(block1) !== normalize(block2);
}

/**
 * Generate a change report comparing two block lists
 */
function generateChangeReportForBlocks(
  currentBlocks: CourseBlock[],
  previousBlocks: CourseBlock[]
): VersionChangeReport {
  const currentBlockIds = new Set(currentBlocks.map((b) => b.id));
  const previousBlockIds = new Set(previousBlocks.map((b) => b.id));
  
  // Create maps for quick lookup
  const currentBlockMap = new Map(currentBlocks.map((b) => [b.id, b]));
  const previousBlockMap = new Map(previousBlocks.map((b) => [b.id, b]));

  // Find added blocks (in current but not in previous)
  const addedBlocks: string[] = [];
  for (const id of currentBlockIds) {
    if (!previousBlockIds.has(id)) {
      addedBlocks.push(id);
    }
  }

  // Find removed blocks (in previous but not in current)
  const removedBlocks: string[] = [];
  for (const id of previousBlockIds) {
    if (!currentBlockIds.has(id)) {
      removedBlocks.push(id);
    }
  }

  // Find modified blocks (in both but different content)
  const modifiedBlocks: string[] = [];
  for (const id of currentBlockIds) {
    if (previousBlockIds.has(id)) {
      const currentBlock = currentBlockMap.get(id)!;
      const previousBlock = previousBlockMap.get(id)!;
      if (areBlocksDifferent(currentBlock, previousBlock)) {
        modifiedBlocks.push(id);
      }
    }
  }

  // Generate human-readable summary
  const summaryParts: string[] = [];
  if (addedBlocks.length > 0) {
    summaryParts.push(`${addedBlocks.length} block(s) added`);
  }
  if (modifiedBlocks.length > 0) {
    summaryParts.push(`${modifiedBlocks.length} block(s) modified`);
  }
  if (removedBlocks.length > 0) {
    summaryParts.push(`${removedBlocks.length} block(s) removed`);
  }
  
  const summary = summaryParts.length > 0 
    ? summaryParts.join(', ')
    : 'No changes';

  return {
    addedBlocks,
    modifiedBlocks,
    removedBlocks,
    summary,
  };
}

/**
 * Generate a change report comparing two course snapshots
 * Requirements: 18.8
 */
export function generateChangeReport(
  currentSnapshot: ExportedCourse,
  previousSnapshot: ExportedCourse
): VersionChangeReport {
  return generateChangeReportForBlocks(currentSnapshot.blocks, previousSnapshot.blocks);
}

export function generateLessonChangeReport(
  currentSnapshot: ExportedLesson,
  previousSnapshot: ExportedLesson
): VersionChangeReport {
  return generateChangeReportForBlocks(currentSnapshot.blocks, previousSnapshot.blocks);
}

/**
 * Create a new CourseVersion from the current course state
 * Requirements: 17.1, 17.2, 17.3, 18.6, 18.7
 */
export function createCourseVersion(input: CreateVersionInput): CourseVersion {
  const {
    courseId,
    publishedBy,
    isMajorEdit = false,
    label,
    snapshot,
    previousVersion,
  } = input;

  // Calculate version number
  const versionNumber = previousVersion 
    ? previousVersion.versionNumber + 1 
    : 1;

  // Generate change report if there's a previous version
  const changeReport = previousVersion
    ? generateChangeReport(snapshot, previousVersion.snapshot)
    : undefined;

  const version: CourseVersion = {
    id: generateVersionId(),
    courseId,
    versionNumber,
    publishedBy,
    publishedAt: new Date().toISOString(),
    isMajorEdit,
    snapshot,
    changeReport,
  };

  // Add label if provided
  if (label) {
    version.label = label;
  }

  return version;
}

export function createLessonVersion(input: CreateLessonVersionInput): LessonVersion {
  const {
    lessonId,
    publishedBy,
    isMajorEdit = false,
    label,
    snapshot,
    previousVersion,
  } = input;

  const versionNumber = previousVersion ? previousVersion.versionNumber + 1 : 1;

  const changeReport = previousVersion
    ? generateLessonChangeReport(snapshot, previousVersion.snapshot)
    : undefined;

  const version: LessonVersion = {
    id: generateVersionId(),
    lessonId,
    versionNumber,
    publishedBy,
    publishedAt: new Date().toISOString(),
    isMajorEdit,
    snapshot,
    changeReport,
  };

  if (label) {
    version.label = label;
  }

  return version;
}

/**
 * Compare two versions and return the differences
 * Requirements: 17.5
 */
export function compareVersions(
  version1: CourseVersion,
  version2: CourseVersion
): VersionChangeReport {
  return generateChangeReport(version2.snapshot, version1.snapshot);
}

export function compareLessonVersions(
  version1: LessonVersion,
  version2: LessonVersion
): VersionChangeReport {
  return generateLessonChangeReport(version2.snapshot, version1.snapshot);
}

/**
 * Create a draft content state from a version snapshot (for rollback)
 * Requirements: 17.6
 */
export function rollbackToVersion(version: CourseVersion): {
  courseMeta: { id: string; title: string; description: string; workflowState: 'draft' };
  lessons: Record<string, Lesson>;
  blocks: Record<string, CourseBlock>;
  lessonOrder: string[];
} {
  const { snapshot } = version;

  // Convert arrays back to records
  const lessons: Record<string, Lesson> = {};
  const lessonOrder: string[] = [];
  for (const lesson of snapshot.lessons) {
    lessons[lesson.id] = lesson;
    lessonOrder.push(lesson.id);
  }

  const blocks: Record<string, CourseBlock> = {};
  for (const block of snapshot.blocks) {
    blocks[block.id] = block;
  }

  return {
    courseMeta: {
      id: snapshot.id,
      title: snapshot.title,
      description: snapshot.description,
      workflowState: 'draft', // Rollback always creates a draft
    },
    lessons,
    blocks,
    lessonOrder,
  };
}

export function rollbackLessonToVersion(version: LessonVersion): {
  lesson: Lesson;
  blocks: Record<string, CourseBlock>;
} {
  const { snapshot } = version;

  const blocks: Record<string, CourseBlock> = {};
  for (const block of snapshot.blocks) {
    blocks[block.id] = block;
  }

  // Snapshot contains a full Lesson (ExportedLesson extends Lesson)
  const { blocks: _blocksArray, ...publishedLesson } = snapshot;
  const lesson: Lesson = {
    ...publishedLesson,
    workflowState: 'draft',
    updatedAt: new Date().toISOString(),
  };

  return {
    lesson,
    blocks,
  };
}

/**
 * Export course to JSON string for export-player consumption
 * Requirements: 18.6
 */
export function exportCourseToJson(snapshot: ExportedCourse): string {
  return JSON.stringify(snapshot);
}

/**
 * Parse exported course JSON
 */
export function parseExportedCourse(json: string): ExportedCourse {
  return JSON.parse(json) as ExportedCourse;
}
